// src/stores/attendanceEmployeeStore.js
import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/hrm/attendanceEmployee.service";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";
import { parseAttendanceDateTime } from "@/utils/attendanceDateTime";

export const useAttendanceEmployeeStore = defineStore(
  "attendanceEmployee",
  () => {
    const records = ref([]);
    const loading = ref(false);
    const pagination = reactive({ current_page: 1, per_page: 25, total: 0 });

    const attendanceSummary = ref(null); // { total_work_days, total_leave, total_holidays, total_halfdays, from, to }
    const attendanceSeries = ref([]); // [{ date,label,present,absent,leave,holiday,halfday }]
    const attendanceRows = ref([]); // paginated rows
    const attendanceTotal = ref(0);
    const attendancePageSize = ref(25);
    const attendanceCurrent = ref(1);
    const attendanceLoading = ref(false);

    // policy/settings + schedule
    const settings = reactive({
      policy: "lenient", // 'lenient' | 'strict'
      unscheduledMode: "default_shift", // 'default_shift' | 'free_clock'
      earlyWindowMinutes: 30,
      strictLatestAfterStart: 60,
      graceMinutes: 10,
      defaultShiftStart: "09:00",
      defaultShiftEnd: "17:00",
      defaultShiftBreakMinutes: 60,
      allowMultipleShiftsPerDay: false,
      timezone: null,
    });

    const mySchedule = ref(null); // { date:'YYYY-MM-DD', start_time:'HH:mm', end_time:'HH:mm', break_minutes:number } | null
    const upcoming = ref([]);

    // hero + cards
    const state = reactive({
      isClockedIn: false,
      hasStaleOpenPunch: false,
      staleOpenWorkDate: null,
      companyToday: null,
      todayMinutes: 0,
      weekMinutes: 0,
      monthMinutes: 0,
      todayTargetMinutes: 0,
      weekTargetMinutes: 0,
      monthTargetMinutes: 0,
      usesShiftRoster: false,
      todayRecord: null,
      workedDaysMonth: 0,
    });

    const filters = reactive({
      search: "",
      status: "",
      date: "",
      date_from: "",
      date_to: "",
      sort: "recent",
      employee_id: null,
      per_page: 25,
      page: 1,
    });

    // UI banner for punch window violations
    const banner = ref(null);
    function clearBanner() {
      banner.value = null;
    }

    function minutesToLabel(m) {
      const h = Math.floor((m || 0) / 60);
      const mm = (m || 0) % 60;
      return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
    }

    // ---- realtime ticker (unchanged) ----
    let tickHandle = null;
    function computeLiveTodayMinutes() {
      if (!state.isClockedIn || state.hasStaleOpenPunch) return;
      const cin = parseAttendanceDateTime(state.todayRecord?.check_in_at);
      if (!cin) return;
      const diffMin = Math.max(
        0,
        Math.floor((Date.now() - cin.getTime()) / 60000)
      );
      state.todayMinutes = diffMin;
    }
    function startLiveTimer() {
      stopLiveTimer();
      computeLiveTodayMinutes();
      tickHandle = setInterval(computeLiveTodayMinutes, 30000);
    }
    function stopLiveTimer() {
      if (tickHandle) {
        clearInterval(tickHandle);
        tickHandle = null;
      }
    }

    // ---- helpers to compute punch window on the client (match backend) ----
    function parseLocal(dateStr, timeHHmm) {
      return new Date(`${dateStr}T${timeHHmm}:00`);
    }
    // function getEffectiveShiftForToday() {
    //   const s = mySchedule.value;
    //   if (s?.date && s?.start_time && s?.end_time) {
    //     const start = parseLocal(s.date, s.start_time);
    //     const end = parseLocal(s.date, s.end_time);
    //     return { start, end, break: s.break_minutes || 0, hasStart: true };
    //   }
    //   // unscheduled
    //   if (settings.unscheduledMode === "default_shift") {
    //     const d = new Date();
    //     const y = d.getFullYear(),
    //       m = `0${d.getMonth() + 1}`.slice(-2),
    //       day = `0${d.getDate()}`.slice(-2);
    //     const date = `${y}-${m}-${day}`;
    //     const start = parseLocal(date, settings.defaultShiftStart);
    //     const end = parseLocal(date, settings.defaultShiftEnd);
    //     return {
    //       start,
    //       end,
    //       break: settings.defaultShiftBreakMinutes,
    //       hasStart: true,
    //     };
    //   }
    //   // free clock
    //   return { start: null, end: null, break: 0, hasStart: false };
    // }

    function getEffectiveShiftForToday() {
      const s = mySchedule.value;
      if (s?.date && s?.start_time && s?.end_time) {
        const start = parseLocal(s.date, s.start_time);
        let end = parseLocal(s.date, s.end_time);

        // Overnight shift (e.g. 19:00–04:30)
        if (end <= start) {
          end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        return { start, end, break: s.break_minutes || 0, hasStart: true };
      }

      if (settings.unscheduledMode === "default_shift") {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const date = `${y}-${m}-${day}`;

        const start = parseLocal(date, settings.defaultShiftStart);
        let end = parseLocal(date, settings.defaultShiftEnd);
        if (end <= start) {
          end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        return {
          start,
          end,
          break: settings.defaultShiftBreakMinutes,
          hasStart: true,
        };
      }

      // free clock
      return { start: null, end: null, break: 0, hasStart: false };
    }

    function dayBounds(dateStr) {
      const open = parseLocal(dateStr, "00:00");
      const close = parseLocal(dateStr, "23:59");
      close.setSeconds(59, 999);
      return { open, close };
    }

    function getPunchWindow(now = new Date()) {
      const s = mySchedule.value;
      const { start, end, hasStart } = getEffectiveShiftForToday();

      if (!hasStart && settings.unscheduledMode === "free_clock") {
        const d = new Date();
        const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return { ...dayBounds(date), enforce: false };
      }

      if (!hasStart) {
        const d = new Date();
        const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return { ...dayBounds(date), enforce: true };
      }

      const date = s?.date || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      if (s?.punch_window_open && s?.punch_window_close) {
        let open = parseLocal(date, s.punch_window_open);
        let close = parseLocal(date, s.punch_window_close);
        if (close <= open) {
          close = new Date(close.getTime() + 24 * 60 * 60 * 1000);
        }
        return { open, close, enforce: true };
      }

      let open;
      let close;
      if (settings.policy === "strict") {
        open = s?.min_start_time
          ? parseLocal(date, s.min_start_time)
          : new Date(start.getTime() - settings.earlyWindowMinutes * 60000);
        close = s?.max_start_time
          ? parseLocal(date, s.max_start_time)
          : new Date(start.getTime() + settings.strictLatestAfterStart * 60000);
      } else {
        open = s?.min_start_time ? parseLocal(date, s.min_start_time) : dayBounds(date).open;
        close = s?.max_end_time ? parseLocal(date, s.max_end_time) : end;
        if (close < end) close = end;
      }

      return { open, close, enforce: true };
    }

    function canPunchInNow(now = new Date()) {
      if (
        state.todayRecord?.check_out_at &&
        !settings.allowMultipleShiftsPerDay
      ) {
        return false;
      }

      const { open, close, enforce } = getPunchWindow(now);
      if (!enforce) return true;

      return now >= open && now <= close;
    }

    function punchGuardReason() {
      if (
        state.todayRecord?.check_out_at &&
        !settings.allowMultipleShiftsPerDay
      ) {
        return "Shift ended. Come back tomorrow.";
      }
      return "You can punch in only near your scheduled shift time.";
    }

    function scheduledBaselineMinutes() {
      const s = mySchedule.value;
      if (s?.scheduled_minutes) {
        return Number(s.scheduled_minutes);
      }
      const { start, end, break: br } = getEffectiveShiftForToday();
      if (start && end) {
        return Math.max(0, Math.floor((end - start) / 60000) - (br || 0));
      }
      return 8 * 60;
    }

    // ----------------------------
    // API calls
    // ----------------------------
    async function fetchSettings() {
      try {
        const res = await svc.getAttendanceSettings();
        if (res?.data) Object.assign(settings, res.data);
      } catch {
        /* keep defaults */
      }
    }

    async function fetchToday(params = {}) {
      try {
        const res = await svc.today(params);
        state.isClockedIn = !!res.data?.is_clocked_in;
        state.hasStaleOpenPunch = !!res.data?.has_stale_open_punch;
        state.staleOpenWorkDate = res.data?.stale_open_work_date || null;
        state.companyToday = res.data?.company_today || null;
        state.todayMinutes = res.data?.today_minutes ?? 0;
        state.weekMinutes = res.data?.week_minutes ?? 0;
        state.monthMinutes = res.data?.month_minutes ?? 0;
        state.todayTargetMinutes = res.data?.today_target_minutes ?? 0;
        state.weekTargetMinutes = res.data?.week_target_minutes ?? 0;
        state.monthTargetMinutes = res.data?.month_target_minutes ?? 0;
        state.usesShiftRoster = !!res.data?.uses_shift_roster;
        state.todayRecord = res.data?.record ?? null;
        state.workedDaysMonth = res.data?.worked_days_month ?? 0;
        if (res.data?.timezone) {
          settings.timezone = res.data.timezone;
        }
        if (state.isClockedIn && !state.hasStaleOpenPunch) startLiveTimer();
        else stopLiveTimer();
      } catch (e) {
        console.error(e);
        stopLiveTimer();
      }
    }

    async function fetchRecords(params = {}) {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
        records.value = [];
        pagination.current_page = 1;
        pagination.per_page = 25;
        pagination.total = 0;
        return;
      }

      loading.value = true;
      try {
        const res = await svc.list({ ...filters, ...params });
        const data = res.data;
        records.value = data.data || [];
        if (data.meta) {
          pagination.current_page = data.meta.current_page;
          pagination.per_page = data.meta.per_page;
          pagination.total = data.meta.total;
        } else {
          pagination.current_page = 1;
          pagination.per_page = filters.per_page;
          pagination.total = records.value.length;
        }
      } catch (e) {
        toast(
          e?.response?.data?.message || "Failed to load attendance",
          "error"
        );
      } finally {
        loading.value = false;
      }
    }

    // async function fetchMySchedule(dateStr = null) {
    //   try {
    //     const res = await svc.getMySchedule({
    //       params: dateStr ? { date: dateStr } : {},
    //     });
    //     mySchedule.value = res.data || null;
    //   } catch {
    //     mySchedule.value = null;
    //   }
    // }

    async function fetchMySchedule(dateStr = null) {
      try {
        const params = dateStr ? { date: dateStr } : {};
        const res = await svc.getMySchedule(params);
        mySchedule.value = res.data || null;
      } catch {
        mySchedule.value = null;
      }
    }

    async function fetchMyUpcoming() {
      try {
        const res = await svc.getMyUpcomingSchedule();
        upcoming.value = res.data || [];
      } catch {
        upcoming.value = [];
      }
    }

    async function punchIn(payload = {}) {
      clearBanner();
      if (!canPunchInNow(new Date())) {
        banner.value = punchGuardReason();
        //toast(banner.value, "warning");
        return;
      }
      try {
        const res = await svc.punchIn({
          time: new Date().toISOString(),
          ...payload,
        });
        toast("Clocked in", "success");
        state.isClockedIn = true;
        state.todayRecord = res.data || state.todayRecord;
        startLiveTimer();
        await fetchToday();
        await fetchRecords({ page: 1 });
      } catch (e) {
        toast(e?.response?.data?.message || "Punch in failed", "error");
        throw e;
      }
    }

    async function punchOut(payload = {}) {
      clearBanner();
      try {
        const res = await svc.punchOut({
          time: new Date().toISOString(),
          ...payload,
        });
        toast("Clocked out", "success");
        stopLiveTimer();
        state.isClockedIn = false;
        state.todayRecord = res.data || state.todayRecord;
        await fetchToday();
        await fetchRecords({ page: pagination.current_page });
      } catch (e) {
        toast(e?.response?.data?.message || "Punch out failed", "error");
        throw e;
      }
    }

    async function remove(id) {
      try {
        await svc.destroy(id);
        toast("Attendance deleted", "success");
        await fetchToday();
        await fetchRecords({ page: 1 });
      } catch (e) {
        toast(e?.response?.data?.message || "Delete failed", "error");
        throw e;
      }
    }

    async function fetchMyAttendanceSummary(params = {}) {
      if (attendanceLoading.value) return;
      attendanceLoading.value = true;
      try {
        const { data } = await svc.getMySummary(params);

        attendanceSummary.value = data?.summary || null;
        attendanceSeries.value = Array.isArray(data?.series) ? data.series : [];

        return data;
      } catch (e) {
        attendanceSummary.value = null;
        attendanceSeries.value = [];
        console.error(e);
      } finally {
        attendanceLoading.value = false;
      }
    }

    async function fetchAttendanceReport(params = {}) {
      if (attendanceLoading.value) return;
      attendanceLoading.value = true;
      try {
        const { data } = await reportsService.attendance(params);

        attendanceSummary.value = data?.summary || null;
        attendanceSeries.value = Array.isArray(data?.series) ? data.series : [];

        const rows = data?.rows || {};
        attendanceRows.value = rows.data || [];
        attendanceTotal.value = rows.total || 0;
        attendancePageSize.value = rows.per_page || params.per_page || 25;
        attendanceCurrent.value = rows.current_page || params.page || 1;

        return data;
      } catch (e) {
        toast(
          e?.response?.data?.message ||
            e.message ||
            "Failed to load attendance report",
          "error"
        );
        throw e;
      } finally {
        attendanceLoading.value = false;
      }
    }

    async function exportAttendanceCSV(params = {}) {
      try {
        const res = await reportsService.exportAttendanceCSV(params);
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const from = params.from || "start";
        const to = params.to || "end";
        a.download = `attendance-report-${from}_to_${to}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast(
          e?.response?.data?.message || e.message || "Failed to export CSV",
          "error"
        );
        throw e;
      }
    }

    async function exportAttendancePDF(params = {}) {
      try {
        const res = await reportsService.exportAttendancePDF(params);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const from = params.from || "start";
        const to = params.to || "end";
        a.download = `attendance-report-${from}_to_${to}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast(
          e?.response?.data?.message || e.message || "Failed to export PDF",
          "error"
        );
        throw e;
      }
    }

    return {
      records,
      loading,
      pagination,
      filters,
      settings,
      mySchedule,
      upcoming,
      state,
      banner,
      clearBanner,
      punchGuardReason,
      minutesToLabel,
      scheduledBaselineMinutes,
      canPunchInNow,
      fetchSettings,
      fetchToday,
      fetchRecords,
      fetchMySchedule,
      fetchMyUpcoming,
      punchIn,
      punchOut,
      remove,
      startLiveTimer,
      stopLiveTimer,
      // ✅ report
      attendanceSummary,
      attendanceSeries,
      attendanceRows,
      attendanceTotal,
      attendancePageSize,
      attendanceCurrent,
      attendanceLoading,
      fetchMyAttendanceSummary,
      fetchAttendanceReport,
      exportAttendanceCSV,
      exportAttendancePDF,
    };
  }
);
